import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Download, Loader2, Ruler, Instagram, Mail, Phone, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, getPartnerToken, PARTNER_API } from "../../lib/partnerApi";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export default function PartnerApplicationDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo index or null

  useEffect(() => {
    if (!getPartnerToken()) {
      nav("/partner/login");
      return;
    }
    partnerApi
      .get(`/partner/applications/${id}`)
      .then((r) => setApp(r.data))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Application not found");
        nav("/partner/applications");
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  const downloadSedcard = async () => {
    setDownloading(true);
    try {
      const token = getPartnerToken();
      const res = await fetch(`${PARTNER_API}/partner/applications/${id}/sedcard.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (app?.full_name || "applicant").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      link.download = `ege-models-${safeName}-${app?.reference || "app"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Comp card downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-500" size={20} />
      </div>
    );
  }

  if (!app) return null;

  const photos = app.photos || [];
  const revealed = !!app.contact_revealed;

  return (
    <div data-testid="partner-app-detail-page" className="min-h-screen bg-white text-[#0A0A0A]">
      {/* Top bar */}
      <div className="border-b border-black/10 sticky top-0 z-30 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/partner/applications"
            data-testid="back-to-applications"
            className="flex items-center gap-2 text-sm text-zinc-700 hover:text-black"
          >
            <ChevronLeft size={16} /> All applications
          </Link>
          <button
            data-testid="download-sedcard"
            onClick={downloadSedcard}
            disabled={downloading}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Comp card PDF
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1.6fr_1fr] gap-12">
        {/* Left — photos */}
        <div>
          <p className="label-overline text-zinc-500">{app.reference}</p>
          <h1 className="font-serif text-6xl mt-3 leading-none">{app.full_name}</h1>
          <p className="text-sm text-zinc-500 mt-4">Applied {fmtDate(app.created_at)}</p>

          {photos.length > 0 ? (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((src, i) => (
                <button
                  key={i}
                  data-testid={`photo-thumb-${i}`}
                  onClick={() => setLightbox(i)}
                  className="relative aspect-[3/4] overflow-hidden bg-zinc-100 group"
                >
                  <img
                    src={src}
                    alt={`Digital ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    style={{ objectPosition: "center 20%" }}
                  />
                  <span className="absolute top-2 left-2 bg-black text-white text-[10px] px-1.5 py-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-10 text-sm text-zinc-500">No photos submitted.</p>
          )}
        </div>

        {/* Right — vitals + contact */}
        <aside>
          <div className="border border-black/10 p-6 space-y-6">
            <div>
              <p className="label-overline text-zinc-500">Vitals</p>
              <dl className="mt-4 space-y-3 text-sm">
                <Row label="Division" value={app.gender ? capitalize(app.gender) : "—"} />
                <Row label="Age" value={app.age ? `${app.age} y` : "—"} />
                <Row
                  label="Height"
                  value={
                    app.height_cm ? (
                      <span className="inline-flex items-center gap-1">
                        <Ruler size={12} /> {app.height_cm} cm
                      </span>
                    ) : (
                      "—"
                    )
                  }
                />
                <Row label="Location" value={[app.city, app.country].filter(Boolean).join(", ") || "—"} />
                <Row
                  label="Instagram"
                  value={
                    app.instagram ? (
                      <a
                        href={`https://instagram.com/${String(app.instagram).replace(/^@/, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:underline"
                      >
                        <Instagram size={12} /> {String(app.instagram).replace(/^@/, "")}
                      </a>
                    ) : (
                      "—"
                    )
                  }
                />
              </dl>
            </div>

            <div className="border-t border-black/10 pt-6">
              <p className="label-overline text-zinc-500">Contact</p>
              {revealed ? (
                <dl className="mt-4 space-y-3 text-sm" data-testid="contact-revealed">
                  <Row
                    label="Email"
                    value={
                      app.email ? (
                        <a href={`mailto:${app.email}`} className="inline-flex items-center gap-1 hover:underline">
                          <Mail size={12} /> {app.email}
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Row
                    label="Phone"
                    value={
                      app.phone ? (
                        <a href={`tel:${app.phone}`} className="inline-flex items-center gap-1 hover:underline">
                          <Phone size={12} /> {app.phone}
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                </dl>
              ) : (
                <div data-testid="contact-hidden" className="mt-4 bg-zinc-50 border border-dashed border-black/10 p-4 text-xs text-zinc-600 flex items-start gap-3">
                  <EyeOff size={14} className="mt-0.5 shrink-0" />
                  <p>
                    Contact details are held by the studio. Request a release
                    from Ege Models management at{" "}
                    <a href="mailto:info@egemodels.com" className="underline">info@egemodels.com</a>{" "}
                    quoting reference {app.reference}.
                  </p>
                </div>
              )}
            </div>

            {(app.biography || app.experience) && (
              <div className="border-t border-black/10 pt-6 space-y-4">
                {app.biography && (
                  <div>
                    <p className="label-overline text-zinc-500">Bio</p>
                    <p className="mt-2 text-sm text-zinc-800 leading-relaxed whitespace-pre-line">
                      {app.biography}
                    </p>
                  </div>
                )}
                {app.experience && (
                  <div>
                    <p className="label-overline text-zinc-500">Experience</p>
                    <p className="mt-2 text-sm text-zinc-800 leading-relaxed whitespace-pre-line">
                      {app.experience}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Lightbox — portal into body so `position:fixed` isn't trapped by
          the .page-fade transform ancestor. */}
      {lightbox !== null && photos[lightbox] &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <img
              src={photos[lightbox]}
              alt=""
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-white text-sm"
              onClick={() => setLightbox(null)}
            >
              Close ×
            </button>
          </div>,
          document.body
        )
      }
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-4">
      <dt className="w-24 shrink-0 text-[11px] uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="text-zinc-900">{value}</dd>
    </div>
  );
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
